import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { Client } from 'src/providers/client/client';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';

import { ComplianceApiService } from 'src/shared/services/compliance-api.service';

@Component({
  templateUrl: 'add-compliance-item.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AddComplianceItemPage implements OnInit {
  private client = inject(Client);
  private fb = inject(UntypedFormBuilder);
  private navCtrl = inject(CustomNavController);
  private storage = inject(TidyStorage);
  private rightSidePanelService = inject(RightSidePanelService);
  private util = inject(Util);
  private cdr = inject(ChangeDetectorRef);
  private complianceApi = inject(ComplianceApiService);
  readonly windowService = inject(WindowService);

  form: UntypedFormGroup;
  submitted = false;
  loaded = false;
  isRightSideContent = true;
  dialogParams: any;
  addressItems: any[] = [];
  errorMessage: string;
  saveItemAction = () => this.saveItem();

  bucketItems = [
    { viewValue: 'Blockers', value: 'blockers' },
    { viewValue: 'Before Listing', value: 'before_listing' },
    { viewValue: 'Operate', value: 'operate' },
    { viewValue: 'Taxes', value: 'taxes' },
    { viewValue: 'Admin', value: 'admin' },
    { viewValue: 'Recommended', value: 'recommended' },
  ];

  frequencyItems = [
    { viewValue: 'None', value: '' },
    { viewValue: 'Ongoing', value: 'ongoing' },
    { viewValue: 'Recurring', value: 'recurring' },
    { viewValue: 'Monthly', value: 'monthly' },
    { viewValue: 'Quarterly', value: 'quarterly' },
    { viewValue: 'Annually', value: 'annually' },
  ];

  constructor() {
    this.form = this.fb.group({
      property: ['', Validators.required],
      name: ['', Validators.required],
      bucket: ['operate', Validators.required],
      what_to_do: [''],
      how_to_do_it: [''],
      frequency: [''],
      next_due_at: [''],
      jurisdiction: [''],
      notes: [''],
      is_tax: [false],
      tax_rate_cents: [null],
    });
  }

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.rightSidePanelService.setDialogPageTitle('Add Compliance Item');
    if (this.isRightSideContent) {
      this.dialogParams = await this.storage.retrieve('dialog-params');
    }
    await this.loadAddresses();

    if (this.dialogParams?.selectedAddressId) {
      this.form.patchValue({ property: this.dialogParams.selectedAddressId });
    }

    this.loaded = true;
    this.cdr.markForCheck();
  }

  async loadAddresses() {
    try {
      const addresses = await this.client.getAddresses();
      this.addressItems = addresses.map(addr => ({
        viewValue: addr.address_name || addr.address1,
        value: addr.id,
      }));
    } catch (err) {
      this.util.showError('Failed to load properties', 5000);
    }
  }

  async saveItem() {
    this.submitted = true;
    this.errorMessage = null;
    if (!this.form.valid) {
      this.errorMessage = 'Please fill in the required fields.';
      this.cdr.markForCheck();
      return;
    }
    try {
      const formValue = this.form.value;
      const taxRateCents = formValue.is_tax && formValue.tax_rate_cents !== null && formValue.tax_rate_cents !== undefined
        ? Number(formValue.tax_rate_cents)
        : undefined;
      await firstValueFrom(this.complianceApi.createItem({
        address_id: Number(formValue.property),
        params: {
          bucket: formValue.bucket,
          name: formValue.name,
          what_to_do: formValue.what_to_do || undefined,
          how_to_do_it: formValue.how_to_do_it || undefined,
          frequency: formValue.frequency || undefined,
          next_due_at: formValue.next_due_at || undefined,
          jurisdiction: formValue.jurisdiction || undefined,
          notes: formValue.notes || undefined,
          is_tax: !!formValue.is_tax,
          tax_rate_cents: taxRateCents,
        },
      }));
      await this.storage.save('shouldReloadPageAfterClosingSideRail', true);
      if (this.isRightSideContent) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.navigateBack('compliance');
      }
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to save item';
      this.util.showError(this.errorMessage, 6000);
      this.cdr.markForCheck();
    }
  }
}
